import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@apollo/client";
import { useTheme } from "@mui/material/styles";
import { GET_CONTROLS_WITH_CHECKS } from "../../../../apis/settingsApis/framework-apis/frameworkApis";

const labelSx = (theme) => ({
  display: "block",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.customColors?.text?.strong ?? "#374151",
  mb: 0.75,
  "& .required": { color: theme.customColors?.status?.error?.main ?? "#EF4444" },
});

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "& fieldset": {
      border: "1px solid #D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#9CA3AF",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.2)",
    },
    "& .MuiInputBase-input": {
      color: theme.customColors?.text?.primary ?? "#374151",
      fontSize: "0.9375rem",
      padding: "10px 14px",
      "&::placeholder": {
        color: "#9CA3AF",
        opacity: 1,
      },
    },
  },
});

function AddCustomPolicyDialog({ open, onClose, onNext, initialPolicyName = "", initialSelectedControls = [] }) {
  const theme = useTheme();
  const [policyName, setPolicyName] = useState(initialPolicyName);
  const [selectedControls, setSelectedControls] = useState(initialSelectedControls);

  const { data, loading } = useQuery(GET_CONTROLS_WITH_CHECKS, {
    skip: !open,
  });

  useEffect(() => {
    if (!open) {
      setPolicyName("");
      setSelectedControls([]);
    } else {
      setPolicyName(initialPolicyName ?? "");
      setSelectedControls(Array.isArray(initialSelectedControls) ? initialSelectedControls : []);
    }
  }, [open, initialPolicyName, initialSelectedControls]);

  const rawControls =
    data?.getControlsWithChecks?.data && Array.isArray(data.getControlsWithChecks.data)
      ? data.getControlsWithChecks.data
      : [];
  const controls = Array.from(
    new Map(
      rawControls.map((c) => {
        const key = (c?.controlsTitle || c?.controlsName || String(c?.controlsId) || "").trim().toLowerCase();
        return [key, c];
      })
    ).values()
  );

  const handleClose = () => {
    onClose?.();
  };

  const handleNext = () => {
    if (!policyName || selectedControls.length === 0) return;
    onNext?.({ policyName, selectedControls });
  };

  const canGoNext = policyName.trim() && selectedControls.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          border: `1px solid ${theme.customColors?.border?.subtle ?? "#E5E7EB"}`,
          boxShadow: theme.customColors?.shadow?.main ?? "0 2px 8px #00000010",
        },
      }}
    >
      <DialogTitle fontWeight="bold" sx={{ alignItems: "center" }}>
        Add custom policy
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: theme.customColors?.text?.iconMuted ?? "#64748B",
            "&:hover": { backgroundColor: theme.customColors?.background?.hover ?? "#FAFAFA" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: theme.customColors?.background?.main ?? "#FFFFFF",
        }}
        dividers
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}>
          <Box>
            <Typography component="label" sx={labelSx(theme)}>
              Policy name <span className="required">*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter policy name"
              value={policyName}
              onChange={(e) => setPolicyName(e.target.value)}
              variant="outlined"
              size="small"
              sx={fieldSx(theme)}
            />
          </Box>

          <Box>
            <Typography component="label" sx={labelSx(theme)}>
              Map to controls <span className="required">*</span>
            </Typography>
            <Autocomplete
              multiple
              options={controls}
              sx={{
                borderColor: theme.customColors?.border?.main,
                backgroundColor: theme.customColors?.background?.main,
                "& .MuiAutocomplete-option": {
                  justifyContent: "flex-start",
                },
              }}
              loading={loading}
              value={selectedControls}
              onChange={(_, value) => setSelectedControls(value)}
              getOptionLabel={(option) =>
                option.controlsTitle || option.controlsName || ""
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    py: 1,
                    gap: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.customColors.text.primary, width: "100%" }}>
                    {option.controlsTitle || option.controlsName || ""}
                  </Typography>
                  {option.controlsDescription && (
                    <Typography variant="caption" sx={{ color: theme.customColors.text.secondary, width: "100%" }}>
                      {option.controlsDescription}
                    </Typography>
                  )}
                </Box>
              )}
              isOptionEqualToValue={(opt, val) => {
                const name = (o) => (o?.controlsTitle || o?.controlsName || "").trim().toLowerCase();
                return name(opt) === name(val);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.controlsId}
                    label={option.controlsTitle || option.controlsName}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${theme.customColors?.border?.main ?? "#E8EAED"}`,
                      backgroundColor: theme.customColors?.background?.main ?? "#FFFFFF",
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    "Search and select controls"
                  }
                  variant="outlined"
                  size="small"
                  sx={fieldSx(theme)}
                />
              )}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                fontSize: "0.8125rem",
                color: theme.customColors?.text?.secondary ?? "#6B7280",
              }}
            >
              Associate this policy with controls from your frameworks.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.customColors?.border?.subtle ?? "#E5E7EB"}`,
          backgroundColor: theme.customColors?.background?.main ?? "#FFFFFF",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            color: theme.customColors?.text?.secondary ?? "#6B7280",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canGoNext}
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.palette._blue.dark,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddCustomPolicyDialog;
